/**
 * Client-only media utilities
 * These functions depend on @pathwright/ux and should only be used in client code
 */
import type { Media, MediaInput, MediaType } from "@pathwright/media-utils"
import { MEDIA_TYPES, MuxFileInput, createMuxDownloadURL, getMediaTypeFromMimetype } from "@pathwright/media-utils"
import type { PickerFileMetadata, PickerResponse } from "@pathwright/ux/src/components/filestack/types"
import { createFilestackDownloadURL } from "@pathwright/ux/src/components/filestack/utils"
import get from "lodash/get"

type MuxData = {
  download?: {
    url?: string
  }
}

export const createMediaDownloadURL = (
  mediaItem: Partial<Media & { filestackHandle?: string }> = {},
  muxData?: MuxData
): string => {
  if (mediaItem.muxVideoID) {
    if (!muxData) {
      console.warn("No Mux video data — can't get a download URL")
      return ""
    }

    return createMuxDownloadURL(
      get(muxData, "download.url", ""),
      mediaItem.name
    )
  }

  const handle = mediaItem.filestackHandle || mediaItem.externalID

  if (handle) {
    return createFilestackDownloadURL(handle, mediaItem.name)
  }

  // Unable to create a download URL at this point.
  return ""
}

type UploadOptions = {
  mediaType: MediaType
  accountID?: string | number
  useMux?: boolean
}

export const uploadToMedia = (
  dataArray: PickerResponse["filesUploaded"] | MuxFileInput[],
  { mediaType, accountID = "", useMux = false }: UploadOptions
): MediaInput[] => {
  const returned: MediaInput[] = dataArray.map(
    (
      file: Partial<PickerFileMetadata> &
        Partial<MuxFileInput> & { id?: string }
    ): MediaInput => {
      const {
        mimetype,
        url,
        filename,
        size,
        handle,
        type,
        muxVideoID = null,
        id
      } = file

      return {
        // id: useMux ? muxVideoID || "" : id,
        description: "",
        tags: [] as string[],
        fileSize: size,
        accountID,
        muxVideoID,
        type: useMux
          ? MEDIA_TYPES.VIDEO
          : (getMediaTypeFromMimetype(mimetype!) as MediaType),
        url: useMux ? "" : url!,
        downloadURL: useMux ? "" : url!,
        mimetype: useMux ? type! : mimetype! || "application/*",
        name: useMux ? filename || "New Video" : filename!,
        externalID: useMux ? muxVideoID : handle,
        encodingStatus: useMux ? "" : mediaType === "VIDEO" ? "new" : ""
      }
    }
  )

  return returned
}
